/**
 * CAMORI 配送関連コンポーネント
 * - DeliveryFee: 商品詳細の送料表示
 * - MiniLeadTime: 商品カードの最短日数表示
 * - DeliveryAreaSelector: 配送エリア選択モーダル
 * - CartDeliveryFee: カート個別商品の送料表示
 * - CartDeliveryTotal: カート送料合計表示
 */

/* ==========================================================================
   共通ユーティリティ
   ========================================================================== */

function getPrefIdFromCookie() {
  let prefId = document.cookie.replace(/(?:(?:^|.*;\s*)customer_pref\s*\=\s*([^;]*).*$)|^.*$/, "$1");
  return prefId || 13; // デフォルト: 東京
}

function setPrefIdToCookie(prefId) {
  document.cookie = "customer_pref=" + prefId + "; path=/";
}

/* 倉庫受け取り。カート限定の選択肢なので customer_pref とは別に持つ。
   同じCookieに混ぜると、商品ページの送料表示が該当する県を見つけられなくなる。 */
const PICKUP_VALUE = 'pickup';

function isPickupSelected() {
  return document.cookie.replace(/(?:(?:^|.*;\s*)customer_pickup\s*\=\s*([^;]*).*$)|^.*$/, "$1") === '1';
}

function setPickupSelected(on) {
  document.cookie = on ? "customer_pickup=1; path=/" : "customer_pickup=; path=/; max-age=0";
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
}

/* ==========================================================================
   配送日計算ユーティリティ

   新JSONデータ構造:
   {
     "shipping_days": [2, 4, 6],  // 出荷曜日 (0=日, 1=月, 2=火, 3=水, 4=木, 5=金, 6=土)
     "cutoff_hour": 10,           // 締め時間
     "rows": [
       [配送名, 県番号, 県名, 市区町村, リードタイム, 時間帯, 備考, 配達パターン]
       // index:  0      1      2       3          4        5      6       7
     ]
   }

   旧JSONデータ構造:
   {
     "rows": [
       [ID, 配送方法, ???, 県番号, ???, 市区町村名, リードタイム, 時間帯, 備考]
       // index: 0    1     2     3     4       5          6        7      8
     ]
   }

   配達パターン (新構造のみ):
   - 7: 毎日配達可能
   - 6: 火曜休み
   - 4: 火木日休み
   - 2: 木日のみ配達可能
   ========================================================================== */

// 配達可能日パターン定義 (0=日, 1=月, 2=火, 3=水, 4=木, 5=金, 6=土)
const DELIVERY_PATTERNS = {
  7: [0, 1, 2, 3, 4, 5, 6], // 毎日
  6: [0, 1, 3, 4, 5, 6],    // 火曜休み (2=火を除外)
  4: [1, 3, 5, 6],          // 火木日休み (0=日, 2=火, 4=木を除外)
  2: [0, 4]                 // 木日のみ (4=木, 0=日のみ)
};

/**
 * データ構造が新形式かどうかを判定
 * @param {Object} data - deliveryTermデータ
 * @returns {boolean}
 */
function isNewDataStructure(data) {
  return data && Array.isArray(data.shipping_days);
}

/**
 * 行データから各フィールドを取得（新旧両対応）
 * @param {Array} row - データ行
 * @param {boolean} isNew - 新形式かどうか
 * @returns {Object}
 */
function getRowFields(row, isNew) {
  if (isNew) {
    return {
      prefId: row[1],
      cityName: row[3],
      leadTime: row[4],
      timeSlot: row[5],
      note: row[6],
      pattern: row[7] || 7
    };
  } else {
    // 旧形式
    return {
      prefId: row[3],
      cityName: row[5],
      leadTime: row[6],
      timeSlot: row[7],
      note: row[8],
      pattern: 7  // 旧形式は毎日配達可能とする
    };
  }
}

/**
 * 次の出荷日を取得
 * @param {Date} now - 現在日時
 * @param {number[]} shippingDays - 出荷曜日配列 [2,4,6] = 火木土
 * @param {number} cutoffHour - 締め時間
 * @returns {Date} 次の出荷日
 */
function getNextShippingDate(now, shippingDays, cutoffHour) {
  const result = new Date(now);
  const currentDay = result.getDay();
  const currentHour = result.getHours();

  // 締め時間を過ぎていたら翌日から探す
  let startDay = currentDay;
  if (currentHour >= cutoffHour) {
    result.setDate(result.getDate() + 1);
    startDay = result.getDay();
  }

  // 最大7日間で次の出荷日を探す
  for (let i = 0; i < 7; i++) {
    const checkDay = (startDay + i) % 7;
    if (shippingDays.includes(checkDay)) {
      result.setDate(result.getDate() + i);
      return result;
    }
  }

  // 見つからない場合は翌日を返す（フォールバック）
  return result;
}

/**
 * 指定曜日が配達可能かチェック
 * @param {number} dayOfWeek - 曜日 (0=日, 6=土)
 * @param {number} pattern - 配達パターン (7, 6, 4, 2)
 * @returns {boolean}
 */
function isDeliveryDay(dayOfWeek, pattern) {
  const allowedDays = DELIVERY_PATTERNS[pattern];
  if (!allowedDays) return true; // パターン不明なら毎日可
  return allowedDays.includes(dayOfWeek);
}

/**
 * 配達予定日を計算
 * @param {Date} shippingDate - 出荷日
 * @param {number} leadTime - リードタイム日数
 * @param {number} pattern - 配達パターン
 * @returns {Date} 配達予定日
 */
function getDeliveryDate(shippingDate, leadTime, pattern) {
  const result = new Date(shippingDate);
  result.setDate(result.getDate() + leadTime);

  // 配達不可曜日なら次の配達可能日に繰り越し
  for (let i = 0; i < 7; i++) {
    if (isDeliveryDay(result.getDay(), pattern)) {
      return result;
    }
    result.setDate(result.getDate() + 1);
  }

  return result;
}

/**
 * 日付を「M月D日」形式でフォーマット
 * @param {Date} date
 * @returns {string}
 */
function formatDeliveryDate(date) {
  return (date.getMonth() + 1) + '月' + date.getDate() + '日';
}

/**
 * 配達パターンから休日テキストを生成
 * @param {number} pattern
 * @returns {string}
 */
function getDeliveryPatternText(pattern) {
  switch (pattern) {
    case 7: return '';
    case 6: return '（火曜配達不可）';
    case 4: return '（火・木・日 配達不可）';
    case 2: return '（木・日のみ配達可）';
    default: return '';
  }
}

/* ==========================================================================
   DeliveryFee - 商品詳細ページの送料表示
   ========================================================================== */

class DeliveryFee extends HTMLElement {
  constructor() {
    super();
    this.deliveryFees = JSON.parse(this.dataset.deliveryFees);
    this.RenderDeliveryFee();
    if (this.querySelector('select')) {
      this.querySelector('select').addEventListener('change', this.onSelectPref.bind(this));
    }
  }

  RenderDeliveryFee() {
    this.getPref();
    let fee = '';
    let pricewithfee = '';
    let prefName = '-';
    if (this.pref && this.pref.price !== null) {
      fee = formatCurrency(this.pref.price);
      pricewithfee = formatCurrency(this.pref.price + parseInt(this.dataset.itemPrice));
      if (this.querySelector('.deliver-label')) {
        this.querySelector('.deliver-label').innerHTML = 'への送料';
      }
      if (this.querySelector('.totalPrice')) {
        this.querySelector('.totalPrice').innerHTML = '<span>＝ 合計</span><span class="price-with-fee"></span>';
      }
      prefName = this.pref.name;
    } else {
      if (this.querySelector('.deliver-label')) {
        this.querySelector('.deliver-label').innerHTML = 'へはお届けできません';
      }
      if (this.querySelector('.totalPrice')) {
        this.querySelector('.totalPrice').innerHTML = '';
      }
      prefName = this.pref.name;
    }
    this.querySelector('.fee').innerHTML = fee;
    if (this.querySelector('.price-with-fee')) {
      this.querySelector('.price-with-fee').innerHTML = pricewithfee;
    }
    if (this.querySelector('.pref-name')) {
      this.querySelector('.pref-name').innerHTML = prefName;
    }
  }

  getPref() {
    let prefId = getPrefIdFromCookie();

    const select = this.querySelector('select');
    if (select) {
      Array.from(select.options).forEach((option) => {
        if (option.value == prefId) {
          option.selected = true;
        } else {
          option.selected = false;
        }
      });
    }

    let pref = null;
    this.deliveryFees.forEach((pref) => {
      if (pref.id == prefId) this.pref = pref;
    });
  }

  onSelectPref(event) {
    event.preventDefault();
    setPrefIdToCookie(event.target.value);
    this.RenderDeliveryFee();
    let mini = document.querySelector('mini-lead-time');
    if (mini) {
      mini.RenderDeliveryTerm();
    }
  }
}

customElements.define('delivery-fee', DeliveryFee);

/* ==========================================================================
   MiniLeadTime - 商品カードの最短配送日数表示
   ========================================================================== */

class MiniLeadTime extends HTMLElement {
  constructor() {
    super();
    this.prefId = 13;
    this.itemId = this.dataset.itemId;
    let leadTime = document.querySelector('#modal-item-' + this.itemId + ' delivery-area-selector');
    if (leadTime) {
      // 配送名がどのキーワード分岐にも一致しないと data-delivery-term が出力されない。
      // その場合も例外で要素ごと死なず「---」表示に落とす
      this.deliveryTerm = leadTime.dataset.deliveryTerm ? JSON.parse(leadTime.dataset.deliveryTerm) : null;
      this.deliveryType = leadTime.dataset.deliveryType || null;
    }
    this.RenderDeliveryTerm();
  }

  getPref() {
    this.prefId = getPrefIdFromCookie();
  }

  /**
   * 都道府県の最短お届け日を取得
   * @returns {Date|null}
   */
  getEarliestDeliveryDate() {
    this.getPref();
    if (!this.deliveryTerm || !this.deliveryTerm.rows) return null;

    const isNew = isNewDataStructure(this.deliveryTerm);
    const { shipping_days, cutoff_hour } = this.deliveryTerm;
    const now = new Date();
    const shippingDate = getNextShippingDate(now, shipping_days || [2, 4, 6], cutoff_hour || 10);

    let earliestDate = null;

    this.deliveryTerm.rows.forEach((row) => {
      const fields = getRowFields(row, isNew);

      if (fields.prefId == this.prefId && fields.leadTime) {
        const deliveryDate = getDeliveryDate(new Date(shippingDate), fields.leadTime, fields.pattern);
        if (!earliestDate || deliveryDate < earliestDate) {
          earliestDate = deliveryDate;
        }
      }
    });

    return earliestDate;
  }

  RenderDeliveryTerm() {
    const earliestDate = this.getEarliestDeliveryDate();
    const el = this.querySelector('.min-lead-time');

    if (el) {
      this.getPref();
      if (this.prefId == 47 && this.deliveryType === 'yhc') {
        el.innerHTML = '沖縄は要事前確認';
      } else if (earliestDate) {

        const month = earliestDate.getMonth() + 1;
        const day = earliestDate.getDate();
        el.innerHTML = '最短<span class="delivery-date-highlight">' + month + '/' + day + '</span>着';
      } else {
        el.innerHTML = '---';
      }
    }
  }
}

customElements.define('mini-lead-time', MiniLeadTime);

/* ==========================================================================
   DeliveryAreaSelector - 配送エリア選択（モーダル内）
   ========================================================================== */

class DeliveryAreaSelector extends HTMLElement {
  constructor() {
    super();
    this.deliveryFees = this.getDeliveryFees();
    this.deliveryTerm = this.dataset.deliveryTerm ? JSON.parse(this.dataset.deliveryTerm) : null;
    this.isNewStructure = isNewDataStructure(this.deliveryTerm);
    this.cities = [];
    this.setPrefecture();
    this.setCities();
    this.itemId = this.dataset.itemId;
  }

  getDeliveryFees(pref) {
    let deliveryFees = document.querySelector('delivery-fee');
    if (deliveryFees) {
      return JSON.parse(deliveryFees.dataset.deliveryFees);
    } else if (this.dataset.deliveryFees) {
      return JSON.parse(this.dataset.deliveryFees);
    } else {
      return null;
    }
  }

  setPrefecture(pref) {
    let target = this.querySelector('[name="prefecture"]');
    let prefId = getPrefIdFromCookie();
    if (!prefId) {
      setPrefIdToCookie(13);
      prefId = 13;
    }

    this.deliveryFees.forEach((pref) => {
        let option = document.createElement('option');
        option.value = pref.id;
        option.text = pref.name;
        if (prefId == pref.id) {
          option.selected = true;
        }
        target.appendChild(option);
    });

    this.setDeliveryFee();

    target.addEventListener('change', this.onChangePrefecture.bind(this));
  }

  onChangePrefecture(event) {
    event.preventDefault();
    setPrefIdToCookie(event.target.value);
    document.cookie = "customer_city=; path=/";
    let deliveryFee = document.querySelector('delivery-fee');
    if (deliveryFee) {
      deliveryFee.RenderDeliveryFee();
    }
    let minis = document.querySelectorAll('mini-lead-time');
    if (minis) {
      minis.forEach((mini) => {
        mini.RenderDeliveryTerm();
      });
    }
    // カートの送料も更新
    document.querySelectorAll('cart-delivery-fee').forEach(el => el.render());
    let cartTotal = document.querySelector('cart-delivery-total');
    if (cartTotal) cartTotal.render();

    this.setDeliveryFee();
    this.setCities();
  }

  isOkinawaYhc() {
    return getPrefIdFromCookie() == 47 && this.dataset.deliveryType === 'yhc';
  }

  setDeliveryFee() {
    let fee = this.getDeliveryFee();
    const feeEl = this.querySelector('.delivery-fee');
    const termEl = this.querySelector('.term-date');
    const termTimeEl = this.querySelector('.term-time');
    const termNoteEl = this.querySelector('.term-note');

    // 市区町村セレクトを表示に戻す
    let citySelect = this.querySelector('.sikuchoson');
    if (citySelect) citySelect.style.display = '';

    if (fee === null) {
      if (feeEl) feeEl.innerHTML = '配達不可';
      if (termEl) termEl.innerHTML = ' - ';
    } else {
      if (feeEl) feeEl.innerHTML = fee + ' 円';
      // 都道府県選択時は最短日を表示
      const earliestDate = this.getEarliestDeliveryDateForPref();
      if (termEl) {
        termEl.innerHTML = earliestDate
          ? formatDeliveryDate(earliestDate) + '～'
          : '市区町村を選択してください';
      }
    }

    let target = this.querySelector('[name="cityCode"]');
    if (!target) return false;

    this.setCities()
  }

  getDeliveryFee() {
    let prefId = getPrefIdFromCookie();
    let fee = '-';
    this.deliveryFees.forEach((pref) => {
        if (pref.id == prefId) fee = pref.price;
    });

    return fee;
  }

  /**
   * 都道府県の最短お届け日を取得
   */
  getEarliestDeliveryDateForPref() {
    const prefId = getPrefIdFromCookie();
    const isNew = isNewDataStructure(this.deliveryTerm);
    const { shipping_days, cutoff_hour, rows } = this.deliveryTerm;
    const now = new Date();
    const shippingDate = getNextShippingDate(now, shipping_days || [2, 4, 6], cutoff_hour || 10);

    let earliestDate = null;

    rows.forEach((row) => {
      const fields = getRowFields(row, isNew);
      if (fields.prefId == prefId && fields.leadTime) {
        const deliveryDate = getDeliveryDate(new Date(shippingDate), fields.leadTime, fields.pattern);
        if (!earliestDate || deliveryDate < earliestDate) {
          earliestDate = deliveryDate;
        }
      }
    });

    return earliestDate;
  }

  initCities() {
    let target = this.querySelector('[name="cityCode"]');
    if (!target) return;
    target.innerHTML = '';
    let option = document.createElement('option');
    option.value = '';
    option.text = '- 市区町村を選択してください -';
    target.appendChild(option);

    const termDateEl = this.querySelector('.term-date');
    const termTimeEl = this.querySelector('.term-time');
    const termNoteEl = this.querySelector('.term-note');

    if (termDateEl) termDateEl.innerHTML = '';
    if (termTimeEl) termTimeEl.innerHTML = '';
    if (termNoteEl) termNoteEl.innerHTML = '';
  }

  setCities() {
    let target = this.querySelector('[name="cityCode"]');
    if (!target) return;

    let prefId = getPrefIdFromCookie();
    let cityName = document.cookie.replace(/(?:(?:^|.*;\s*)customer_city\s*\=\s*([^;]*).*$)|^.*$/, "$1");
    cityName = decodeURIComponent(cityName);

    this.initCities();
    this.cities = [];
    this.isNewStructure = isNewDataStructure(this.deliveryTerm);

    this.deliveryTerm.rows.forEach((row) => {
      const fields = getRowFields(row, this.isNewStructure);
      if (fields.prefId == prefId) {
        let option = document.createElement('option');
        option.value = fields.cityName;
        option.text = fields.cityName;
        if (cityName === fields.cityName) {
          option.selected = true;
        }
        target.appendChild(option);
        this.cities.push(row);
      }
    });

    this.renderDescription();

    // 既存のリスナーを削除してから追加
    target.removeEventListener('change', this.onChangeCity);
    this.onChangeCity = this.onChangeCity.bind(this);
    target.addEventListener('change', this.onChangeCity);
  }

  onChangeCity(event) {
    event.preventDefault();
    const cityName = event.target.value;
    document.cookie = "customer_city=" + encodeURIComponent(cityName) + "; path=/";
    this.renderDescription();
  }

  renderDescription() {
    let cityName = document.cookie.replace(/(?:(?:^|.*;\s*)customer_city\s*\=\s*([^;]*).*$)|^.*$/, "$1");
    cityName = decodeURIComponent(cityName);

    const termDateEl = this.querySelector('.term-date');
    const termTimeEl = this.querySelector('.term-time');
    const termNoteEl = this.querySelector('.term-note');

    // 選択された市区町村のデータを取得
    let targetRow = null;
    this.cities.forEach((row) => {
      const fields = getRowFields(row, this.isNewStructure);
      if (fields.cityName === cityName) targetRow = row;
    });

    const okinawaNote = this.isOkinawaYhc() ? '沖縄への配送は事前確認が必要です' : '';

    if (targetRow) {
      const fields = getRowFields(targetRow, this.isNewStructure);
      const deliveryDateStr = this.getDeliverTerm(targetRow);
      const patternText = getDeliveryPatternText(fields.pattern);

      if (termDateEl) {
        termDateEl.innerHTML = deliveryDateStr || '配達不可';
      }
      if (termTimeEl) {
        termTimeEl.innerHTML = fields.timeSlot || '';
      }
      if (termNoteEl) {
        termNoteEl.innerHTML = (fields.note || '') + patternText + (okinawaNote ? '<br>' + okinawaNote : '');
      }
    } else if (!cityName) {
      // 最短日を表示
      const earliestDate = this.getEarliestDeliveryDateForPref();
      if (termDateEl) {
        termDateEl.innerHTML = earliestDate
          ? formatDeliveryDate(earliestDate) + '～'
          : '市区町村を選択してください';
      }
      if (termTimeEl) termTimeEl.innerHTML = '';
      if (termNoteEl) termNoteEl.innerHTML = okinawaNote;
    } else {
      if (termDateEl) termDateEl.innerHTML = '';
      if (termTimeEl) termTimeEl.innerHTML = '';
      if (termNoteEl) termNoteEl.innerHTML = okinawaNote;
    }
  }

  /**
   * 配達予定日を計算
   * @param {Array} row - 市区町村データ行
   * @returns {string|false}
   */
  getDeliverTerm(row) {
    const fields = getRowFields(row, this.isNewStructure);

    if (!fields.leadTime) return false;

    const { shipping_days, cutoff_hour } = this.deliveryTerm;
    const now = new Date();
    const shippingDate = getNextShippingDate(now, shipping_days || [2, 4, 6], cutoff_hour || 10);
    const deliveryDate = getDeliveryDate(shippingDate, fields.leadTime, fields.pattern);

    return formatDeliveryDate(deliveryDate);
  }
}

customElements.define('delivery-area-selector', DeliveryAreaSelector);

/* ==========================================================================
   CartDeliveryFee - カート個別商品の送料表示
   ========================================================================== */

class CartDeliveryFee extends HTMLElement {
  constructor() {
    super();
    this.deliveryFees = JSON.parse(this.dataset.deliveryFees || 'null');
    this.render();
  }

  getFeeForPref(prefId) {
    if (!this.deliveryFees) return null;
    const pref = this.deliveryFees.find(p => p.id == prefId);
    return pref || null;
  }

  render() {
    const prefId = getPrefIdFromCookie();
    const pref = this.getFeeForPref(prefId);
    const valueEl = this.querySelector('.delivery-fee-value');

    if (valueEl) {
      if (isPickupSelected()) {
        valueEl.textContent = formatCurrency(0);
      } else if (pref && pref.price !== null) {
        valueEl.textContent = formatCurrency(pref.price);
      } else if (pref && pref.price === null) {
        valueEl.textContent = '配送不可';
      } else {
        valueEl.textContent = '-';
      }
    }
  }
}

customElements.define('cart-delivery-fee', CartDeliveryFee);

/* ==========================================================================
   CartDeliveryTotal - カート送料合計表示
   ========================================================================== */

class CartDeliveryTotal extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('#cart-pref-select');
    this.initSelect();
    this.render();

    if (this.select) {
      this.select.addEventListener('change', this.onSelectChange.bind(this));
    }
  }

  initSelect() {
    if (!this.select) return;
    const selected = isPickupSelected() ? PICKUP_VALUE : getPrefIdFromCookie();
    Array.from(this.select.options).forEach(option => {
      option.selected = option.value == selected;
    });
  }

  onSelectChange(event) {
    const value = event.target.value;
    if (value === PICKUP_VALUE) {
      /* 配送先そのものは変えない。受け取りをやめたときに元の県へ戻せるようにしておく */
      setPickupSelected(true);
    } else {
      setPickupSelected(false);
      setPrefIdToCookie(value);
    }
    this.render();
    this.updateAllItemFees();
  }

  updateAllItemFees() {
    document.querySelectorAll('cart-delivery-fee').forEach(el => {
      el.render();
    });
  }

  getCartData() {
    const dataEl = document.getElementById('cart-delivery-data');
    if (!dataEl) return [];
    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      return [];
    }
  }

  calculateTotals() {
    const prefId = getPrefIdFromCookie();
    const pickup = isPickupSelected();
    const items = this.getCartData();
    let deliveryTotal = 0;
    let productTotal = 0;
    let hasUndeliverable = false;

    items.forEach(item => {
      productTotal += item.price;

      /* 倉庫でお渡しするので配送は発生しない。配送不可の県も関係なくなる */
      if (item.deliveryFees && !pickup) {
        const pref = item.deliveryFees.find(p => p.id == prefId);
        if (pref && pref.price !== null) {
          deliveryTotal += pref.price * item.quantity;
        } else if (pref && pref.price === null) {
          hasUndeliverable = true;
        }
      }
    });

    return {
      deliveryTotal,
      productTotal,
      grandTotal: productTotal + deliveryTotal * 100, // productTotalは銭単位
      hasUndeliverable
    };
  }

  render() {
    const totals = this.calculateTotals();

    const deliveryValueEl = this.querySelector('.cart-delivery-total__value');
    const grandTotalEl = this.querySelector('.cart-delivery-grand-total__value');

    if (deliveryValueEl) {
      if (totals.hasUndeliverable) {
        deliveryValueEl.textContent = '一部配送不可の商品があります';
      } else {
        deliveryValueEl.textContent = formatCurrency(totals.deliveryTotal);
      }
    }

    if (grandTotalEl) {
      if (totals.hasUndeliverable) {
        grandTotalEl.textContent = '-';
      } else {
        grandTotalEl.textContent = formatCurrency(totals.grandTotal / 100);
      }
    }
  }
}

customElements.define('cart-delivery-total', CartDeliveryTotal);
